/**
 * 幾何学計算ユーティリティ
 * Drawing コンポーネントで使用する座標計算関数
 */

// 三角形描画用の角度定数
export const ANGLE_1 = 0
export const ANGLE_2 = coordToRadian(0, 0, 1, -2)
export const ANGLE_3 = coordToRadian(0, 0, -1, -2)
export const ANGLE_4 = Math.PI
export const ANGLE_5 = coordToRadian(0, 0, -1, 2)
export const ANGLE_6 = coordToRadian(0, 0, 1, 2)

/**
 * 2点間の座標から角度（ラジアン）を計算
 * @param x1 - 始点X座標
 * @param y1 - 始点Y座標
 * @param x2 - 終点X座標
 * @param y2 - 終点Y座標
 * @returns ラジアン角度 (0 ~ 2π)
 */
export function coordToRadian(x1: number, y1: number, x2: number, y2: number): number {
    const deltaX = x2 - x1
    const deltaY = y1 - y2

    if (deltaX === 0 && deltaY >= 0)
        return Math.PI / 2
    if (deltaX === 0 && deltaY < 0)
        return Math.PI * 1.5

    const tanA = deltaY / deltaX

    if (deltaX >= 0 && deltaY >= 0)
        return Math.atan(tanA)
    if (deltaX < 0 && deltaY >= 0)
        return Math.PI + Math.atan(tanA)
    if (deltaX < 0 && deltaY < 0)
        return Math.PI + Math.atan(tanA)
    if (deltaX >= 0 && deltaY < 0)
        return 2 * Math.PI + Math.atan(tanA)

    return 0
}

/**
 * 四角形の描画ポイントを計算
 * @param x1 - 始点X座標
 * @param y1 - 始点Y座標
 * @param x2 - 終点X座標
 * @param y2 - 終点Y座標
 * @param headSize - ブラシサイズ
 * @returns 描画ポイント配列 [x1, y1, x2, y2, ...]
 */
export function getSquareLinePoints(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    headSize: number
): number[] {
    // 垂直線（上下方向）
    if (x1 === x2) {
        const topX = x1
        const topY = y1 <= y2 ? y1 : y2
        const bottomY = y1 >= y2 ? y1 : y2
        return [
            topX - headSize / 2, topY - headSize / 2,
            topX + headSize / 2, topY - headSize / 2,
            topX + headSize / 2, bottomY + headSize / 2,
            topX - headSize / 2, bottomY + headSize / 2,
        ]
    }

    // 水平線（左右方向）
    if (y1 === y2) {
        const leftY = y1
        const leftX = x1 <= x2 ? x1 : x2
        const rightX = x1 >= x2 ? x1 : x2
        return [
            leftX - headSize / 2, leftY - headSize / 2,
            rightX + headSize / 2, leftY - headSize / 2,
            rightX + headSize / 2, leftY + headSize / 2,
            leftX - headSize / 2, leftY + headSize / 2,
        ]
    }

    // 対角線（左上 - 右下）
    if ((x1 < x2 && y1 < y2) || (x2 < x1 && y2 < y1)) {
        const topLeftX = x1 < x2 ? x1 : x2
        const topLeftY = y1 < y2 ? y1 : y2
        const bottomRightX = x1 > x2 ? x1 : x2
        const bottomRightY = y1 > y2 ? y1 : y2
        return [
            topLeftX - headSize / 2, topLeftY - headSize / 2,
            topLeftX + headSize / 2, topLeftY - headSize / 2,
            bottomRightX + headSize / 2, bottomRightY - headSize / 2,
            bottomRightX + headSize / 2, bottomRightY + headSize / 2,
            bottomRightX - headSize / 2, bottomRightY + headSize / 2,
            topLeftX - headSize / 2, topLeftY + headSize / 2,
        ]
    }

    // 対角線（左下 - 右上）
    if ((x1 < x2 && y1 > y2) || (x2 < x1 && y2 > y1)) {
        const bottomLeftX = x1 < x2 ? x1 : x2
        const bottomLeftY = y1 > y2 ? y1 : y2
        const topRightX = x1 > x2 ? x1 : x2
        const topRightY = y1 < y2 ? y1 : y2
        return [
            bottomLeftX - headSize / 2, bottomLeftY - headSize / 2,
            topRightX - headSize / 2, topRightY - headSize / 2,
            topRightX + headSize / 2, topRightY - headSize / 2,
            topRightX + headSize / 2, topRightY + headSize / 2,
            bottomLeftX + headSize / 2, bottomLeftY + headSize / 2,
            bottomLeftX - headSize / 2, bottomLeftY + headSize / 2,
        ]
    }

    return []
}

/**
 * 三角形の描画ポイントを計算
 * @param x1 - 始点X座標
 * @param y1 - 始点Y座標
 * @param x2 - 終点X座標
 * @param y2 - 終点Y座標
 * @param headSize - ブラシサイズ
 * @returns 描画ポイント配列 [x1, y1, x2, y2, ...]
 */
export function getTriangleLinePoints(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    headSize: number
): number[] {
    const triangleDeltaRadian = coordToRadian(x1, y1, x2, y2)

    if (triangleDeltaRadian >= ANGLE_1 && triangleDeltaRadian < ANGLE_2)
        return [
            x1 - headSize / 2, y1 + headSize / 2,
            x1, y1 - headSize / 2,
            x2, y2 - headSize / 2,
            x2 + headSize / 2, y2 + headSize / 2,
            x1 + headSize / 2, y1 + headSize / 2,
        ]

    if (triangleDeltaRadian >= ANGLE_2 && triangleDeltaRadian < ANGLE_3)
        return [
            x1 - headSize / 2, y1 + headSize / 2,
            x2 - headSize / 2, y2 + headSize / 2,
            x2, y2 - headSize / 2,
            x2 + headSize / 2, y2 + headSize / 2,
            x1 + headSize / 2, y1 + headSize / 2,
        ]

    if (triangleDeltaRadian >= ANGLE_3 && triangleDeltaRadian < ANGLE_4)
        return [
            x1 - headSize / 2, y1 + headSize / 2,
            x2 - headSize / 2, y2 + headSize / 2,
            x2, y2 - headSize / 2,
            x1, y1 - headSize / 2,
            x1 + headSize / 2, y1 + headSize / 2,
        ]

    if (triangleDeltaRadian >= ANGLE_4 && triangleDeltaRadian < ANGLE_5)
        return [
            x1, y1 - headSize / 2,
            x1 + headSize / 2, y1 + headSize / 2,
            x2 + headSize / 2, y2 + headSize / 2,
            x2 - headSize / 2, y2 + headSize / 2,
            x2, y2 - headSize / 2,
        ]

    if (triangleDeltaRadian >= ANGLE_5 && triangleDeltaRadian < ANGLE_6)
        return [
            x1 - headSize / 2, y1 + headSize / 2,
            x1, y1 - headSize / 2,
            x1 + headSize / 2, y1 + headSize / 2,
            x2 + headSize / 2, y2 + headSize / 2,
            x2 - headSize / 2, y2 + headSize / 2,
        ]

    if (triangleDeltaRadian >= ANGLE_6)
        return [
            x1 - headSize / 2, y1 + headSize / 2,
            x1, y1 - headSize / 2,
            x2, y2 - headSize / 2,
            x2 + headSize / 2, y2 + headSize / 2,
            x2 - headSize / 2, y2 + headSize / 2,
        ]

    return []
}
